import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import { is_client_inbound } from './EscalationAlerts';
import AnnotateImage from '../../../components/annotate/AnnotateImage';
import { BACKSTOP_NOTE, annotation_of, annotation_upload_fields, assistant_meta, first_image_ref, from_media_note, insight_for, insight_label, revision_of, system_meta, triage_label, upload_posted_message } from '../../Chat/user_chat';
import { RECORD_SCREEN_TITLE, STOP_SCREEN_TITLE, fmt_elapsed, use_screen_recorder, voice_note_supported } from './screen_record';
import PendingAttachment, { with_preview } from './pending_attachment';
import { NOT_RECALLING, caret_on_first_line, caret_on_last_line, own_history, recall_move } from './recall_history';

const fmt_ts = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const fmt_size = (n) => {
    if (n == null) return '';
    if (n > 1048576) return (n / 1048576).toFixed(1) + ' MB';
    if (n > 1024) return Math.round(n / 1024) + ' KB';
    return n + ' B';
};

const cs_base = () => {
    const p = (global.config && global.config.cs_analysis_path) || '';
    return String(p).replace(/\/+$/, '');
};

/** Scroll a loaded message into view and flash it — the jump-to-message
 *  landing and the "↻ revised from" line share it. False when the row is not
 *  in the DOM (outside the loaded window). */
const reveal_message = (channel_id, seq) => {
    const target = document.getElementById(`comms-msg-${channel_id}-${seq}`);
    if (!target) return false;
    target.scrollIntoView({ block: 'center' });
    target.style.transition = 'background 1.6s';
    target.style.background = '#fff3c4';
    setTimeout(() => { target.style.background = ''; }, 1800);
    return true;
};

// Binary fetches bypass cs_analysis_fetch (it JSON-parses); cookies + the CSP
// CSRF header still travel so the authorizing proxy accepts us.
const fetch_media_blob = async (message_id, idx) => {
    const res = await fetch(`${cs_base()}/api/v1/comms/media/${message_id}/${idx}`, {
        credentials: 'include',
        headers: { 'X-Requested-With': 'cs-analysis-csp' },
    });
    if (!res.ok) throw new Error('media ' + res.status);
    return res.blob();
};

/** Inline image via the media proxy. Space is RESERVED while loading (async
 *  loads must not push the thread bottom away), and clicking opens the
 *  lightbox. on_media_load lets the thread re-anchor to bottom. */
function MediaImage({ message_id, idx, filename, on_media_load, on_zoom, on_annotate, size }) {
    const [url, set_url] = useState(null);
    const [failed, set_failed] = useState(false);
    const w = size && size.w ? size.w : 240;
    const h = size && size.h ? size.h : 180;
    useEffect(() => {
        let revoke = null; let alive = true;
        fetch_media_blob(message_id, idx)
            .then((b) => { if (!alive) return; revoke = URL.createObjectURL(b); set_url(revoke); })
            .catch(() => { if (alive) set_failed(true); });
        return () => { alive = false; if (revoke) URL.revokeObjectURL(revoke); };
    }, [message_id, idx]);
    return (
        <>
            <div style={{ width: w, height: h, borderRadius: 6, background: '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: url ? 'zoom-in' : 'default' }}
                onClick={() => url && on_zoom && on_zoom({ key: `${message_id}:${idx}`, url, filename, kind: 'image' })}>
                {failed ? <span style={{ fontSize: 11, color: '#95a5a6' }}>(image unavailable)</span>
                    : !url ? <Spinner size='sm' />
                        : <img src={url} alt={filename || 'attachment'} onLoad={on_media_load}
                            onError={() => set_failed(true)}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />}
            </div>
            {url && on_annotate ? (
                // Mark up this image (rect / arrow / draw / text / blur) and send the
                // result as a reply to its message — the thread shows before + after.
                <span onClick={() => on_annotate({ message_id, idx, filename })} title='Draw on this image and send the marked-up copy as a reply'
                    style={{ display: 'inline-block', marginTop: 3, fontSize: 11, color: '#2f6fed', cursor: 'pointer' }}>✎ Annotate</span>
            ) : null}
        </>
    );
}

/** Inline audio player (voice notes) — auto-fetches; they're small. */
function MediaAudio({ message_id, idx }) {
    const [url, set_url] = useState(null);
    const [failed, set_failed] = useState(false);
    useEffect(() => {
        let revoke = null; let alive = true;
        fetch_media_blob(message_id, idx)
            .then((b) => { if (!alive) return; revoke = URL.createObjectURL(b); set_url(revoke); })
            .catch(() => { if (alive) set_failed(true); });
        return () => { alive = false; if (revoke) URL.revokeObjectURL(revoke); };
    }, [message_id, idx]);
    if (failed) return <span style={{ fontSize: 11, color: '#95a5a6' }}>(voice note unavailable)</span>;
    if (!url) return <span style={{ fontSize: 11, color: '#95a5a6' }}>loading voice note…</span>;
    return <audio controls src={url} style={{ height: 32, maxWidth: 260, display: 'block', marginTop: 4 }} />;
}

/** Video player — auto-loads for 1-step play (Nikhil 2026-07-14) up to
 *  AUTO_VIDEO_MAX; bigger files keep the click-to-load button. The box is
 *  fixed-size so async loads never shift the thread's scroll anchor. */
const AUTO_VIDEO_MAX = 12 * 1048576;
function MediaVideo({ message_id, idx, media_ref, on_zoom }) {
    const auto = media_ref.size == null || media_ref.size <= AUTO_VIDEO_MAX;
    const [url, set_url] = useState(null);
    const [busy, set_busy] = useState(false);
    const [failed, set_failed] = useState(false);
    const revoke_ref = useRef(null);
    const load = useCallback(async () => {
        if (busy || url) return;
        set_busy(true);
        try {
            const u = URL.createObjectURL(await fetch_media_blob(message_id, idx));
            revoke_ref.current = u;
            set_url(u);
        } catch (e) { set_failed(true); }
        set_busy(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busy, url, message_id, idx]);
    useEffect(() => {
        if (auto) load();
        return () => { if (revoke_ref.current) URL.revokeObjectURL(revoke_ref.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message_id, idx]);
    if (failed) return <div style={{ marginTop: 4, fontSize: 11, color: '#95a5a6' }}>(video unavailable)</div>;
    if (url) {
        return (
            <div style={{ marginTop: 4 }}>
                <video controls preload='metadata' src={url}
                    style={{ width: 280, height: 180, borderRadius: 6, display: 'block', background: '#000', objectFit: 'contain' }} />
                <span onClick={() => on_zoom && on_zoom({ key: `${message_id}:${idx}`, url, filename: media_ref.filename, kind: 'video' })}
                    style={{ fontSize: 11, color: '#2f6fed', cursor: 'pointer' }}>⤢ pop out</span>
            </div>
        );
    }
    if (auto) {
        return (
            <div style={{ marginTop: 4, width: 280, height: 180, borderRadius: 6, background: '#0b0e11', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size='sm' style={{ color: '#fff' }} />
            </div>
        );
    }
    return (
        <div style={{ marginTop: 4 }}>
            <Button size='sm' outline color='secondary' onClick={load} disabled={busy} style={{ fontSize: 11, padding: '2px 8px' }}>
                {busy ? <Spinner size='sm' /> : '▶'} video{media_ref.size ? ` · ${fmt_size(media_ref.size)}` : ''}
            </Button>
        </div>
    );
}

/** What an attachment says, collapsed under its player/thumbnail (cs-analysis §6.9). */
function InsightLine({ insight }) {
    const [open, set_open] = useState(false);
    if (!insight) return null;
    return (
        <div style={{ marginTop: 3, fontSize: 11 }}>
            <span onClick={() => set_open((v) => !v)} title={open ? 'Hide' : 'Show'} style={{ cursor: 'pointer', color: '#2f6fed' }}>
                {open ? '▾' : '▸'} {insight_label(insight.kind)}
            </span>
            {open ? (
                <div style={{ marginTop: 2, padding: '4px 8px', borderLeft: '2px solid #dfe8f6', color: '#4a5a6a', whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto', lineHeight: 1.4 }}>
                    {insight.text}
                </div>
            ) : null}
        </div>
    );
}

/** One attachment row: images inline, audio/video players, else download chip. */
function Attachment({ message_id, idx, media_ref, on_media_load, on_zoom, on_annotate, insight }) {
    // Hooks BEFORE any conditional return (rules-of-hooks).
    const [busy, set_busy] = useState(false);
    const mime = media_ref.mimetype || '';
    const is_image = mime.indexOf('image/') === 0;
    const download = async () => {
        if (busy) return;
        set_busy(true);
        try {
            const blob = await fetch_media_blob(message_id, idx);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = media_ref.filename || 'attachment';
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 30000);
        } catch (e) { window.alert('Could not download attachment'); }
        set_busy(false);
    };
    if (mime.indexOf('audio/') === 0) return <><MediaAudio message_id={message_id} idx={idx} /><InsightLine insight={insight} /></>;
    if (mime.indexOf('video/') === 0) return <><MediaVideo message_id={message_id} idx={idx} media_ref={media_ref} on_zoom={on_zoom} /><InsightLine insight={insight} /></>;
    if (is_image) return <div style={{ marginTop: 4 }}><MediaImage message_id={message_id} idx={idx} filename={media_ref.filename} on_media_load={on_media_load} on_zoom={on_zoom} on_annotate={on_annotate} /><InsightLine insight={insight} /></div>;
    return (
        <div style={{ marginTop: 4 }}>
            <Button size='sm' outline color='secondary' onClick={download} disabled={busy} style={{ fontSize: 11, padding: '2px 8px' }}>
                {busy ? <Spinner size='sm' /> : '📎'} {media_ref.filename || 'attachment'}{media_ref.size ? ` · ${fmt_size(media_ref.size)}` : ''}
            </Button>
        </div>
    );
}

/** WhatsApp delivery ticks for our outbound messages on bridged channels. */
function WaTicks({ m }) {
    if (m.origin === 'WHATSAPP' || (!m.waUniqueId && !m.waMessageId)) return null;
    const ack = m.waAck != null ? Number(m.waAck) : null;
    const read = ack != null && ack >= 4;
    const delivered = ack != null && ack >= 3;
    const di = m.waDeliveryInfo || {};
    const title = read
        ? `read by ${di.read_count != null ? di.read_count : 'all'}`
        : delivered
            ? `delivered to ${di.delivered_count != null ? di.delivered_count : 'all'}`
            : 'sent to WhatsApp';
    return (
        <span title={title} style={{ marginLeft: 6, fontSize: 11, color: read ? '#2f80ed' : '#95a5a6', letterSpacing: -2 }}>
            {delivered || read ? '✓✓' : '✓'}
        </span>
    );
}

/** Uniform, desktop-friendly message-action icon (Nikhil: too small). */
function MsgAction({ onClick, title, danger, children }) {
    return (
        <span onClick={onClick} title={title}
            style={{ cursor: 'pointer', marginLeft: 8, color: danger ? '#e08b8b' : '#8a97a0',
                fontSize: 15, padding: '2px 4px', borderRadius: 4, display: 'inline-block', lineHeight: 1 }}
            onMouseEnter={(e) => { e.target.style.background = '#eef2f5'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}>
            {children}
        </span>
    );
}

// ── Knowledge plane: rep affordances (cs-analysis /api/v1/knowledge) ──
// Mirrors the ClientTicketModule taxonomy (ClientComms.jsx MODULES).
const KNOWLEDGE_MODULES = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION', 'MES', 'OTHER'];
const conf_color = (c) => (c >= 0.6 ? '#2e9e5b' : c >= 0.4 ? '#8fa3c0' : '#d68910');
const cut_text = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };

/** One product fact the AI suggestion was grounded on. Click the title for
 *  the full text; ✓ confirms it (rep_attested), ✗ flags it wrong / outdated
 *  (rep_flagged, optional reason). Either recomputes the fact's confidence at
 *  once, so the next suggestion already ranks by it. Lives inside the ghost
 *  card, whose click accepts the suggestion — every click here stops
 *  propagation. */
function KnowledgeChip({ unit, channel_id, for_seq }) {
    const [open, set_open] = useState(false);
    const [detail, set_detail] = useState(null);
    const [mode, set_mode] = useState('idle'); // idle | flagging | busy | confirmed | flagged
    const [reason, set_reason] = useState('');
    const stop = (e) => { if (e) { e.stopPropagation(); e.preventDefault(); } };
    const toggle = async (e) => {
        stop(e);
        set_open((o) => !o);
        if (detail) return;
        try {
            const d = await cs_analysis_fetch({ path: `/api/v1/knowledge/units/${unit.id}` });
            set_detail(d);
            if (d && d.mine && d.mine.attested) set_mode('confirmed');
            else if (d && d.mine && d.mine.flagged) set_mode('flagged');
        } catch (err) { set_detail({ body: '(could not load the full text)' }); }
    };
    const signal = async (e, kind) => {
        stop(e);
        if (mode === 'busy') return;
        set_mode('busy');
        try {
            await cs_analysis_fetch({
                path: `/api/v1/knowledge/units/${unit.id}/${kind}`, method: 'POST',
                body: { channelId: channel_id, forSeq: for_seq, ...(kind === 'flag' && reason.trim() ? { reason: reason.trim() } : {}) },
            });
            set_mode(kind === 'flag' ? 'flagged' : 'confirmed');
        } catch (err) { set_mode('idle'); window.alert('Could not record that — please try again.'); }
    };
    const done = mode === 'confirmed' || mode === 'flagged';
    // A draft suggestion hands over the whole unit; an answer's citation carries
    // only id + title, so the confidence arrives with the detail fetch.
    const conf = typeof unit.confidence === 'number' ? unit.confidence : detail && typeof detail.confidence === 'number' ? detail.confidence : null;
    const pct = conf === null ? null : Math.round(conf * 100);
    return (
        <div onClick={stop} style={{ fontSize: 10.5, background: '#fff', border: '1px solid #dfe8f6', borderRadius: 8, padding: '3px 7px', maxWidth: '100%', color: '#4a5a6a', cursor: 'default' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span title={pct === null ? 'confidence — open to load' : `confidence ${pct}%`}
                    style={{ width: 7, height: 7, borderRadius: 4, background: conf === null ? '#dfe8f6' : conf_color(conf), flex: 'none' }} />
                <span onClick={toggle} title='Show the full fact' style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                    📘 {cut_text(unit.title, 64)}
                </span>
                {unit.status === 'STALE' ? <span style={{ color: '#b9770e', whiteSpace: 'nowrap' }}>· possibly outdated</span> : null}
                {done ? (
                    <span style={{ color: mode === 'confirmed' ? '#2e9e5b' : '#c0392b', whiteSpace: 'nowrap' }}>{mode === 'confirmed' ? '✓ confirmed' : '⚑ flagged'}</span>
                ) : mode === 'busy' ? <Spinner size='sm' style={{ width: 10, height: 10 }} /> : (
                    <>
                        <span onClick={(e) => signal(e, 'attest')} title='This fact is right' style={{ cursor: 'pointer', color: '#2e9e5b', fontWeight: 700, padding: '0 2px' }}>✓</span>
                        <span onClick={(e) => { stop(e); set_mode('flagging'); set_open(true); }} title='Wrong or outdated' style={{ cursor: 'pointer', color: '#c0392b', fontWeight: 700, padding: '0 2px' }}>✗</span>
                    </>
                )}
            </div>
            {open ? (
                <div style={{ marginTop: 4, color: '#5d6d7e', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
                    <span style={{ color: '#95a5a6' }}>{unit.module || (detail && detail.module) || ''}{detail && detail.feature ? ` · ${detail.feature}` : ''}{pct === null ? '' : ` · confidence ${pct}%`}</span>
                    <div>{detail ? detail.body : <Spinner size='sm' style={{ width: 10, height: 10 }} />}</div>
                </div>
            ) : null}
            {mode === 'flagging' ? (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }} onClick={stop}>
                    <Input bsSize='sm' value={reason} placeholder='What is wrong or outdated? (optional)' autoFocus
                        onChange={(e) => set_reason(e.target.value)}
                        onKeyDown={(e) => {
                            // The composer's window listener treats Tab / Esc as accept / dismiss.
                            e.stopPropagation();
                            if (e.key === 'Enter') signal(e, 'flag');
                            else if (e.key === 'Escape') { e.preventDefault(); set_mode('idle'); }
                        }}
                        style={{ fontSize: 11, padding: '2px 6px', height: 24 }} />
                    <Button size='sm' color='danger' outline style={{ fontSize: 11, padding: '0 8px' }} onClick={(e) => signal(e, 'flag')}>Flag</Button>
                    <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px' }} onClick={(e) => { stop(e); set_mode('idle'); }}>Cancel</Button>
                </div>
            ) : null}
        </div>
    );
}

/** "Save as knowledge" (💡 on a message): the backend drafts a de-identified
 *  fact from this message read in its conversation window (flash); the rep
 *  edits and saves. Saved facts are DRAFT / REP_ATTESTED with conversation
 *  provenance and, when client-safe, start grounding reply suggestions at
 *  once. The closest existing facts are listed so a near-duplicate becomes a
 *  confirmation instead of a second row. */
function PromoteKnowledgeModal({ channel_id, message, on_close, on_saved }) {
    const [loading, set_loading] = useState(true);
    const [draft, set_draft] = useState(null); // the LLM draft, kept to detect rep edits
    const [source, set_source] = useState(null);
    const [similar, set_similar] = useState([]);
    const [warn, set_warn] = useState(null);
    const [form, set_form] = useState({ title: '', module: 'OTHER', feature: '', body: '', triggers: '', clientSafe: true });
    const [saving, set_saving] = useState(false);
    const [attested, set_attested] = useState({}); // similar unit id → true
    useEffect(() => {
        let alive = true;
        set_loading(true);
        cs_analysis_fetch({ path: '/api/v1/knowledge/promote/draft', method: 'POST', body: { channelId: channel_id, seq: message.seq } })
            .then((r) => {
                if (!alive) return;
                set_source(r && r.source ? r.source : null);
                set_similar((r && r.similar) || []);
                if (r && r.draft) {
                    set_draft(r.draft);
                    set_form({
                        title: r.draft.title || '', module: KNOWLEDGE_MODULES.indexOf(r.draft.module) >= 0 ? r.draft.module : 'OTHER',
                        feature: r.draft.feature || '', body: r.draft.body || '',
                        triggers: (r.draft.triggers || []).join(', '), clientSafe: !!r.draft.clientSafe,
                    });
                    if (!r.draft.isKnowledge) set_warn(`The drafter doubts this is reusable knowledge: ${r.draft.reason || 'no reason given'} You can still save it.`);
                } else {
                    set_warn('Drafting failed — write the fact by hand.');
                }
            })
            .catch((e) => { if (alive) set_warn('Could not draft: ' + ((e && e.message) || 'unknown error') + '. Write the fact by hand.'); })
            .finally(() => { if (alive) set_loading(false); });
        return () => { alive = false; };
    }, [channel_id, message.seq]);
    const upd = (k) => (e) => {
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        set_form((f) => ({ ...f, [k]: v }));
    };
    const triggers_list = form.triggers.split(/[,;\n]+/).map((t) => t.trim()).filter(Boolean);
    const draft_edited = !draft || draft.title !== form.title || draft.body !== form.body || draft.feature !== form.feature
        || draft.module !== form.module || (draft.triggers || []).join(', ') !== form.triggers || !!draft.clientSafe !== !!form.clientSafe;
    const save = async () => {
        if (!form.title.trim() || !form.body.trim()) { window.alert('Title and the fact itself are required.'); return; }
        set_saving(true);
        try {
            const r = await cs_analysis_fetch({
                path: '/api/v1/knowledge/units', method: 'POST',
                body: {
                    module: form.module, feature: form.feature.trim(), title: form.title.trim(), body: form.body.trim(),
                    triggers: triggers_list, clientSafe: !!form.clientSafe, draftEdited: draft_edited,
                    source: source
                        ? { channelId: source.channelId, seq: source.seq, fromSeq: source.fromSeq, toSeq: source.toSeq }
                        : { channelId: channel_id, seq: message.seq, fromSeq: message.seq, toSeq: message.seq },
                },
            });
            on_saved(r);
        } catch (e) { window.alert('Could not save: ' + ((e && e.message) || 'unknown error')); }
        set_saving(false);
    };
    const attest = async (u) => {
        try {
            await cs_analysis_fetch({ path: `/api/v1/knowledge/units/${u.id}/attest`, method: 'POST', body: { channelId: channel_id, forSeq: message.seq } });
            set_attested((a) => ({ ...a, [u.id]: true }));
        } catch (e) { window.alert('Could not record that — please try again.'); }
    };
    const field = { fontSize: 13 };
    const label = { fontSize: 11, color: '#7f8c8d', marginBottom: 2, marginTop: 8 };
    const hint = { color: '#b0b8bd' };
    return (
        <Modal isOpen toggle={on_close} size='lg'>
            <ModalHeader toggle={on_close} style={{ fontSize: 15 }}>💡 Save as knowledge</ModalHeader>
            <ModalBody style={{ paddingTop: 8 }}>
                <div style={{ fontSize: 11.5, color: '#7f8c8d', borderLeft: '3px solid #d6dbdf', padding: '2px 8px', background: '#f6f8f9', borderRadius: 4 }}>
                    <b>{message.authorName || message.authorType}</b>: {cut_text(message.body, 240)}
                </div>
                {loading ? <div style={{ marginTop: 12, fontSize: 12, color: '#8fa3c0' }}><Spinner size='sm' /> drafting from the conversation…</div> : (
                    <>
                        {warn ? <div style={{ marginTop: 10, fontSize: 12, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 6, padding: '5px 8px' }}>{warn}</div> : null}
                        {similar.length ? (
                            <div style={{ marginTop: 10, fontSize: 12, background: '#f4f8ff', border: '1px solid #d9e6ff', borderRadius: 8, padding: '6px 10px' }}>
                                <div style={{ fontWeight: 600, color: '#33414e', marginBottom: 4 }}>Already known? Confirm one of these instead of adding a duplicate.</div>
                                {similar.map((u) => (
                                    <div key={u.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
                                        <span style={{ width: 7, height: 7, borderRadius: 4, background: conf_color(u.confidence || 0), flex: 'none' }} />
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            {u.title} <span style={{ color: '#95a5a6' }}>· {u.module}{u.audience === 'INTERNAL' ? ' · internal' : ''}{u.status === 'STALE' ? ' · possibly outdated' : ''}</span>
                                        </span>
                                        {attested[u.id] ? <span style={{ color: '#2e9e5b', fontSize: 11, whiteSpace: 'nowrap' }}>✓ confirmed</span>
                                            : <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px', border: '1px solid #dde3e8', whiteSpace: 'nowrap' }} onClick={() => attest(u)}>This one is right ✓</Button>}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        <div style={label}>Title <span style={hint}>— one searchable statement of the fact</span></div>
                        <Input value={form.title} onChange={upd('title')} style={field} maxLength={200} />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <div style={{ flex: '0 0 180px' }}>
                                <div style={label}>Module</div>
                                <Input type='select' value={form.module} onChange={upd('module')} style={field}>
                                    {KNOWLEDGE_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
                                </Input>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={label}>Feature / area</div>
                                <Input value={form.feature} onChange={upd('feature')} style={field} maxLength={120} placeholder='e.g. drawer hardware templates' />
                            </div>
                        </div>
                        <div style={label}>The fact <span style={hint}>— general case; no client, project or person names</span></div>
                        <Input type='textarea' rows={6} value={form.body} onChange={upd('body')} style={field} maxLength={4000} />
                        <div style={label}>How clients phrase it <span style={hint}>— comma-separated; used to find this fact</span></div>
                        <Input value={form.triggers} onChange={upd('triggers')} style={field} placeholder='cnc program missing, cut list blank, …' />
                        <label style={{ fontSize: 12, marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input type='checkbox' checked={!!form.clientSafe} onChange={upd('clientSafe')} />
                            Safe to paraphrase to clients <span style={{ color: '#95a5a6' }}>(untick for internal-only: defect workarounds, pricing internals, promises)</span>
                        </label>
                    </>
                )}
            </ModalBody>
            <ModalFooter style={{ padding: '8px 16px' }}>
                <span style={{ fontSize: 11, color: '#95a5a6', marginRight: 'auto' }}>Saved as a draft fact in your name; it grounds AI reply suggestions and any rep can flag it.</span>
                <Button color='light' onClick={on_close} disabled={saving}>Cancel</Button>
                <Button color='primary' onClick={save} disabled={loading || saving || !form.title.trim() || !form.body.trim()}>{saving ? <Spinner size='sm' /> : 'Save knowledge'}</Button>
            </ModalFooter>
        </Modal>
    );
}

/** Media lightbox: full-size image / video pop-out with download. */
/** Full-screen viewer with ◀ ▶ navigation across the conversation's media
 *  (thread window or media gallery). `start` = the clicked item ({key, url?});
 *  other items' blobs are fetched on demand and cached for the session of
 *  this lightbox. ← → and Esc work too. */
function Lightbox({ items, start, on_close, on_jump, on_annotate }) {
    // A `start` the gallery does not know (the composer's pending attachment,
    // not yet a message) is viewed on its own — falling back to item 0 would
    // show the wrong media.
    const in_items = !!(start && items && items.some((x) => x.key === start.key));
    const list = in_items ? items : (start ? [start] : (items || []));
    const [pos, set_pos] = useState(() => {
        const i = start ? list.findIndex((x) => x.key === start.key) : 0;
        return i >= 0 ? i : 0;
    });
    const cache = useRef(new Map()); // key -> blob url
    const [url, set_url] = useState(start && start.url ? start.url : null);
    const [busy, set_busy] = useState(false);
    const item = list[pos];
    useEffect(() => {
        if (start && start.url) cache.current.set(start.key, start.url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!item) return undefined;
        const hit = cache.current.get(item.key);
        if (hit) { set_url(hit); return undefined; }
        let dead = false;
        set_url(null); set_busy(true);
        fetch_media_blob(item.message_id, item.idx)
            .then((b) => {
                if (dead) return;
                const u = URL.createObjectURL(b);
                cache.current.set(item.key, u);
                set_url(u); set_busy(false);
            })
            .catch(() => { if (!dead) set_busy(false); });
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pos]);
    useEffect(() => {
        const h = (e) => {
            if (e.key === 'Escape') on_close();
            else if (e.key === 'ArrowLeft') set_pos((p) => Math.max(0, p - 1));
            else if (e.key === 'ArrowRight') set_pos((p) => Math.min(list.length - 1, p + 1));
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list.length, on_close]);
    useEffect(() => () => {
        // Revoke lightbox-created urls; the opener's own url (start.url) is
        // owned by its thumbnail component — leave that one alone.
        cache.current.forEach((u, k) => {
            if (!(start && start.key === k && start.url === u)) URL.revokeObjectURL(u);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!item) return null;
    const nav = (d, e) => { e.stopPropagation(); set_pos((p) => Math.min(list.length - 1, Math.max(0, p + d))); };
    const arrow = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', fontSize: 38, color: '#dfe6ea', cursor: 'pointer', padding: '18px 14px', userSelect: 'none', zIndex: 2, lineHeight: 1 };
    return (
        <div onClick={on_close}
            style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.86)', zIndex: 2000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            {pos > 0 ? <span style={{ ...arrow, left: 8 }} onClick={(e) => nav(-1, e)}>‹</span> : null}
            {pos < list.length - 1 ? <span style={{ ...arrow, right: 8 }} onClick={(e) => nav(1, e)}>›</span> : null}
            {!url ? (
                busy ? <Spinner style={{ color: '#fff' }} /> : <span style={{ color: '#dfe6ea', fontSize: 13 }}>(media unavailable)</span>
            ) : item.kind === 'video' ? (
                <video controls autoPlay src={url} onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: '86vw', maxHeight: '82vh', borderRadius: 8 }} />
            ) : (
                <img src={url} alt={item.filename || ''} onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: '86vw', maxHeight: '82vh', borderRadius: 8, objectFit: 'contain', background: '#000' }} />
            )}
            <div style={{ marginTop: 10, color: '#dfe6ea', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                {list.length > 1 ? <span style={{ marginRight: 12, color: '#9db2c0' }}>{pos + 1} / {list.length}</span> : null}
                {item.filename || ''}{' '}
                {url ? <a href={url} download={item.filename || 'attachment'} style={{ color: '#8ab8ff', marginLeft: 10 }}>download</a> : null}
                {url && on_annotate && item.kind === 'image' && item.message_id != null ? ( // annotate re-fetches by message; a pending image has none
                    <span onClick={() => on_annotate({ message_id: item.message_id, idx: item.idx, filename: item.filename })}
                        title='Draw on this image and send the marked-up copy as a reply' style={{ cursor: 'pointer', marginLeft: 16, color: '#8ab8ff' }}>✎ annotate</span>
                ) : null}
                {on_jump && item.seq != null ? (
                    <span onClick={() => on_jump(item.seq)} style={{ cursor: 'pointer', marginLeft: 16, color: '#8ab8ff' }}>go to message →</span>
                ) : null}
                <span onClick={on_close} style={{ cursor: 'pointer', marginLeft: 16, color: '#e0888b' }}>close ✕</span>
            </div>
        </div>
    );
}

const REACT_SET = ['👍', '❤️', '✅', '😂', '😮', '🙏'];

/** Aggregated reaction chips + picker. Clicking a chip or a picker emoji
 *  toggles the caller's reaction (server mirrors to WhatsApp when bridged). */
function Reactions({ message, on_react }) {
    const [show_picker, set_show_picker] = useState(false);
    const reactions = message.reactions || [];
    const by_emoji = {};
    reactions.forEach((r) => {
        if (!by_emoji[r.emoji]) by_emoji[r.emoji] = [];
        by_emoji[r.emoji].push(r.senderName || 'someone');
    });
    if (message.deletedAt || message.deleted) return null;
    return (
        <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
            {Object.entries(by_emoji).map(([emoji, names]) => (
                <span key={emoji} title={names.join(', ')} onClick={() => on_react && on_react(message, emoji)}
                    style={{ display: 'inline-block', fontSize: 12, background: '#f1f3f5', borderRadius: 10, padding: '1px 7px', cursor: on_react ? 'pointer' : 'default' }}>
                    {emoji}{names.length > 1 ? ` ${names.length}` : ''}
                </span>
            ))}
            {on_react ? (
                show_picker ? (
                    <span style={{ background: '#fff', border: '1px solid #e3e8ec', borderRadius: 12, padding: '1px 6px' }}>
                        {REACT_SET.map((e) => (
                            <span key={e} style={{ cursor: 'pointer', fontSize: 13, margin: '0 2px' }}
                                onClick={() => { set_show_picker(false); on_react(message, e); }}>{e}</span>
                        ))}
                        <span style={{ cursor: 'pointer', color: '#95a5a6', marginLeft: 4, fontSize: 11 }} onClick={() => set_show_picker(false)}>✕</span>
                    </span>
                ) : (
                    <span onClick={() => set_show_picker(true)} title='React'
                        style={{ cursor: 'pointer', color: '#b0b8bd', fontSize: 12, border: '1px dashed #d5dbe1', borderRadius: 10, padding: '0 6px' }}>+</span>
                )
            ) : null}
        </div>
    );
}

// ── @-mention picker grouping (pure; mention_members.test.js) ────────────
// GET /comms/channels/:id/members returns client-side rows (USER / GUEST)
// and Infurnia-side rows: REP (WhatsApp participant) and STAFF (CS user,
// {name, kind:'STAFF', staffUserId} — added by cs-analysis 2026-09; absent
// on older backends, in which case the team group is simply empty). The
// same rep can appear as both REP and STAFF → one row per name per group.
export const MENTION_GROUPS = [
    { label: 'Client', kinds: ['USER', 'GUEST'] },
    { label: 'Infurnia team', kinds: ['REP', 'STAFF'] },
];
const MENTION_KIND_LABEL = { USER: 'client', GUEST: 'guest', REP: 'rep', STAFF: 'staff' };
export const mention_kind_label = (kind) => MENTION_KIND_LABEL[kind] || String(kind || '').toLowerCase();
export function group_mention_members(members, q, per_group = 6) {
    const needle = String(q || '').toLowerCase();
    const groups = MENTION_GROUPS.map((g) => ({ label: g.label, kinds: g.kinds, members: [] }));
    const seen = new Set();
    (Array.isArray(members) ? members : []).forEach((m) => {
        if (!m || typeof m.name !== 'string' || !m.name.trim()) return;
        if (needle && !m.name.toLowerCase().includes(needle)) return;
        // Unknown kinds stay visible on the client side (today's behaviour), labelled by their raw kind.
        const g = groups.find((x) => x.kinds.includes(m.kind)) || groups[0];
        const key = g.label + '|' + m.name.trim().toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        if (g.members.length < per_group) g.members.push(m);
    });
    return groups.filter((g) => g.members.length).map((g) => ({ label: g.label, members: g.members }));
}

/** Replace raw @<digits> tokens with resolved mention names (WA inbound). */
function render_body_with_mentions(body, waMentions) {
    if (!Array.isArray(waMentions) || !waMentions.length) return body;
    let out = body;
    waMentions.forEach((m) => {
        const digits = String(m.id || '').replace(/@.*$/, '');
        if (digits && m.name) out = out.split('@' + digits).join('@' + m.name);
    });
    return out;
}

/**
 * Staff-side message thread over a comms channel (cs-analysis L0). Shared by
 * the Client Comms tab (org channel + ticket drawers), the Initiatives view,
 * and the cross-org Chat inbox. Polls 15s with seq cursors + advances the
 * read cursor. `send_override(body)` replaces the default channel post —
 * initiatives use it to send through POST /initiatives/:id/updates.
 * WhatsApp parity (2026-07-13): attachments (proxy-served), quote-replies,
 * reaction chips, delivery ticks.
 */
export default function Thread({ channel_id, allow_internal, height = 420, placeholder, send_override, focus_seq, store_id, on_to_ticket, me_id, on_sent }) {
    const [messages, set_messages] = useState([]);
    const [draft, set_draft] = useState('');
    const [internal, set_internal] = useState(false);
    const [busy, set_busy] = useState(false);
    const [loaded, set_loaded] = useState(false);
    const [pending_media, set_pending_media] = useState(null); // {mediaId, filename, mimetype, size}
    const [uploading, set_uploading] = useState(false);
    const [reply_to, set_reply_to] = useState(null); // message object
    const [at_bottom, set_at_bottom] = useState(true);
    const [members, set_members] = useState([]); // mention autocomplete source
    const [mention_q, set_mention_q] = useState(null); // active @query or null
    const mention_groups = mention_q != null ? group_mention_members(members, mention_q) : [];
    // ↑ recalls the last message the rep sent, "like in many chat apps" (team
    // request via Nikhil, 2026-09-15). The list is the rep's own rows in the
    // loaded window for the visibility the composer is in; the cursor indexes
    // it and is NOT_RECALLING when idle. Logic in recall_history.js.
    const [recall_idx, set_recall_idx] = useState(NOT_RECALLING);
    const compose_visibility = allow_internal && internal ? 'INTERNAL' : 'CLIENT_VISIBLE';
    const recall_list = useMemo(() => own_history(messages, me_id, compose_visibility), [messages, me_id, compose_visibility]);
    const [loading_older, set_loading_older] = useState(false);
    const [history_done, set_history_done] = useState(false);
    const [editing, set_editing] = useState(null);
    const [edit_draft, set_edit_draft] = useState('');
    const [forward_msg, set_forward_msg] = useState(null); // message being forwarded
    const [forward_targets, set_forward_targets] = useState(null);
    const [recording, set_recording] = useState(false);
    const [lightbox, set_lightbox] = useState(null); // {url, filename, kind}
    const [annotating, set_annotating] = useState(null); // {message_id, idx, filename, url} — annotation layer open
    const [suggestion, set_suggestion] = useState(null); // {text, confidence, evidenceCount, knowledgeCount, knowledge[], forSeq}
    const [sugg_loading, set_sugg_loading] = useState(false);
    const [promote_msg, set_promote_msg] = useState(null); // message being saved as knowledge (modal open)
    const [notice, set_notice] = useState(null); // transient confirmation line above the composer
    useEffect(() => {
        if (!notice) return undefined;
        const t = setTimeout(() => set_notice(null), 6000);
        return () => clearTimeout(t);
    }, [notice]);
    // Escalation alerter feedback: an inbound message the alerter missed →
    // POST /alerts/report-missed (REP_REPORTED), optional note.
    const [report_msg, set_report_msg] = useState(null); // inbound message being reported
    const [report_note, set_report_note] = useState('');
    const [report_busy, set_report_busy] = useState(false);
    // The accepted suggestion whose knowledge chips stay under the composer
    // while its draft is in the input (UI only — telemetry uses accepted_ref).
    const [grounded, set_grounded] = useState(null);
    const recorder_ref = useRef(null);
    const bottom_ref = useRef(null);
    const scroll_ref = useRef(null);
    const file_ref = useRef(null);
    const input_ref = useRef(null);
    const first_load = useRef(true);
    const last_seq = messages.length ? messages[messages.length - 1].seq : 0;

    // ── AI reply suggestion ("tab and enter and done", Nikhil 2026-07-14) ──
    // When the latest client-visible message is inbound and recent, the
    // backend drafts ONE grounded reply (org corpus + this conversation).
    // Tab inserts it into the composer; Enter sends. Never auto-sent.
    // System lines and the assistant's own answers are not "who spoke last":
    // in a user chat the client's "talk to a person" arrives as client message
    // → SYSTEM needs_human, and the rep still wants a draft for it.
    const last_visible = [...messages].reverse().find((m) => !m.deletedAt && m.visibility === 'CLIENT_VISIBLE' && m.authorType !== 'SYSTEM' && m.authorType !== 'AI');
    const last_inbound_seq =
        last_visible && (last_visible.authorType === 'CLIENT_USER' || last_visible.authorType === 'GUEST')
            ? last_visible.seq : null;
    useEffect(() => {
        if (!loaded || !last_inbound_seq) { set_suggestion(null); return undefined; }
        if (suggestion && suggestion.forSeq === last_inbound_seq) return undefined;
        let stale = false;
        const t = setTimeout(async () => {
            set_sugg_loading(true);
            try {
                const r = await cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/suggest-reply`, method: 'POST' });
                if (!stale && r && r.suggestion) {
                    set_suggestion({ text: r.suggestion, confidence: r.confidence, evidenceCount: r.evidenceCount || 0, knowledgeCount: r.knowledgeCount || 0, knowledge: r.knowledge || [], forSeq: last_inbound_seq });
                } else if (!stale) set_suggestion(null);
            } catch (e) { if (!stale) set_suggestion(null); }
            if (!stale) set_sugg_loading(false);
        }, 500);
        return () => { stale = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, channel_id, last_inbound_seq]);

    // K5 telemetry: what the rep DID with the suggestion (fire-and-forget).
    const accepted_ref = useRef(null); // last accepted suggestion, until sent
    const sugg_feedback = useCallback((action, s, extra) => {
        if (!s) return;
        cs_analysis_fetch({
            path: `/api/v1/comms/channels/${channel_id}/suggest-feedback`, method: 'POST',
            body: { action, suggestion: s.text, forSeq: s.forSeq, confidence: s.confidence, evidenceCount: s.evidenceCount, ...(extra || {}) },
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel_id]);
    useEffect(() => { accepted_ref.current = null; set_grounded(null); set_report_msg(null); set_recall_idx(NOT_RECALLING); }, [channel_id]);
    const dismiss_suggestion = useCallback(() => {
        if (suggestion) sugg_feedback('DISMISSED', suggestion);
        set_suggestion(null);
    }, [suggestion, sugg_feedback]);
    const accept_suggestion = useCallback(() => {
        if (!suggestion) return;
        accepted_ref.current = suggestion;
        sugg_feedback('ACCEPTED', suggestion);
        set_draft(suggestion.text);
        // Keep the citation chips reachable after the ghost card goes (Step-4
        // follow-up): the rep can still confirm / flag what the draft rests on.
        set_grounded(suggestion.knowledge && suggestion.knowledge.length ? suggestion : null);
        set_suggestion(null);
        if (input_ref.current) input_ref.current.focus();
    }, [suggestion, sugg_feedback]);
    // Fallback: explicit ✨ click — bypasses the recency guard, surfaces
    // low-confidence drafts too (marked in the ghost card).
    const request_suggestion = async () => {
        if (sugg_loading) return;
        set_sugg_loading(true);
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/suggest-reply`, method: 'POST', body: { force: true } });
            if (r && r.suggestion) {
                set_suggestion({ text: r.suggestion, confidence: r.confidence, evidenceCount: r.evidenceCount || 0, knowledgeCount: r.knowledgeCount || 0, knowledge: r.knowledge || [], forSeq: r.forSeq || last_inbound_seq || last_seq });
            } else {
                set_suggestion(null);
                window.alert(r && r.reason === 'no_pending_inbound'
                    ? 'Nothing pending from the client to reply to.'
                    : 'No confident suggestion available for this conversation.');
            }
        } catch (e) { window.alert('Suggestion failed — please try again.'); }
        set_sugg_loading(false);
    };
    useEffect(() => {
        if (!suggestion) return undefined;
        const h = (e) => {
            if (e.key === 'Tab' && !draft.trim()) { e.preventDefault(); accept_suggestion(); }
            else if (e.key === 'Escape') dismiss_suggestion();
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [suggestion, draft, accept_suggestion, dismiss_suggestion]);

    // Every image/video in the loaded window, in order — the lightbox
    // navigates across these with ◀ ▶.
    const gallery = useMemo(() => {
        const items = [];
        for (const m of messages) {
            if (m.deletedAt || !Array.isArray(m.mediaRefs)) continue;
            m.mediaRefs.forEach((ref, i) => {
                const mm = ((ref && ref.mimetype) || '').toLowerCase();
                if (mm.indexOf('image/') === 0 || mm.indexOf('video/') === 0) {
                    items.push({
                        key: `${m.id}:${i}`, message_id: m.id, idx: i,
                        kind: mm.indexOf('video/') === 0 ? 'video' : 'image',
                        filename: ref.filename, seq: m.seq,
                    });
                }
            });
        }
        return items;
    }, [messages]);

    const load = useCallback(async (after_seq) => {
        if (!channel_id) return;
        // Background tabs must NOT poll/mark-read — a parked CSP tab would
        // silently advance the cursor and defeat the unread badge + web push
        // (review fix). Reads happen only when the tab is actually visible.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/comms/channels/${channel_id}/messages`,
                // Initial load: jump-to window when focus_seq set, else the tail.
                query: after_seq ? { afterSeq: after_seq, limit: 200 }
                    : focus_seq ? { aroundSeq: focus_seq, limit: 200 }
                        : { tail: 1, limit: 200 },
            });
            const fresh = (resp && resp.messages) || [];
            // A short first window IS the whole history — say so instead of
            // letting scroll-up look broken (Nikhil feedback).
            if (!after_seq && !focus_seq && fresh.length < 200) set_history_done(true);
            if (fresh.length) {
                // Dedupe by id — overlapping polls must not double-append.
                set_messages((cur) => {
                    const base = after_seq ? cur : [];
                    const seen = new Set(base.map((m) => m.id));
                    return [...base, ...fresh.filter((m) => !seen.has(m.id))];
                });
                const max_seq = fresh[fresh.length - 1].seq;
                void cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/read`, method: 'POST', body: { seq: max_seq } });
            } else if (!after_seq) set_messages([]);
        } catch (e) { /* transient — next poll retries */ }
        set_loaded(true);
    }, [channel_id, focus_seq]);

    useEffect(() => {
        set_messages([]); set_loaded(false); set_reply_to(null); set_pending_media(null);
        set_history_done(false); first_load.current = true;
        load(0);
        cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/members` })
            .then((r) => set_members((r && r.members) || []))
            .catch(() => set_members([]));
    }, [channel_id, load]);
    useEffect(() => {
        if (!channel_id) return undefined;
        const t = setInterval(() => load(last_seq), 15000);
        // Catch up immediately when the tab becomes visible again.
        const on_vis = () => { if (document.visibilityState === 'visible') load(last_seq); };
        document.addEventListener('visibilitychange', on_vis);
        return () => { clearInterval(t); document.removeEventListener('visibilitychange', on_vis); };
    }, [channel_id, last_seq, load]);
    useEffect(() => {
        const el = scroll_ref.current;
        if (!el || !messages.length) return;
        if (first_load.current) {
            first_load.current = false;
            // Jump-to-message: center + flash the target.
            if (focus_seq && reveal_message(channel_id, focus_seq)) return;
            // Default: land at the LATEST message, instantly (no animation).
            el.scrollTop = el.scrollHeight;
            return;
        }
        // Later batches: only follow if the reader is already near the bottom —
        // a poll landing mid-history-read must not yank them down.
        const near_bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
        if (near_bottom && bottom_ref.current) bottom_ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [messages.length]);

    // History paging: reaching the top loads the older window (beforeSeq),
    // preserving the reader's position; also powers the jump-to-bottom pill.
    const on_scroll = async (e) => {
        const el = e.target;
        set_at_bottom(el.scrollHeight - el.scrollTop - el.clientHeight < 160);
        if (el.scrollTop < 40 && !loading_older && !history_done && messages.length) {
            set_loading_older(true);
            const oldest = messages[0].seq;
            try {
                const r = await cs_analysis_fetch({
                    path: `/api/v1/comms/channels/${channel_id}/messages`,
                    query: { beforeSeq: oldest, limit: 100 },
                });
                const older = (r && r.messages) || [];
                if (!older.length) set_history_done(true);
                else {
                    const prev_height = el.scrollHeight;
                    set_messages((cur) => {
                        const seen = new Set(cur.map((m) => m.id));
                        return [...older.filter((m) => !seen.has(m.id)), ...cur];
                    });
                    // Keep the viewport anchored on the message the reader was at.
                    requestAnimationFrame(() => { el.scrollTop += el.scrollHeight - prev_height; });
                }
            } catch (err) { /* retry on next reach-top */ }
            set_loading_older(false);
        }
    };

    const jump_to_bottom = () => {
        const el = scroll_ref.current;
        if (el) el.scrollTop = el.scrollHeight;
    };

    // Async image loads grow the content; if the reader was at the bottom,
    // keep them there (placeholders reserve most of the space already).
    const on_media_load = () => {
        const el = scroll_ref.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 220) el.scrollTop = el.scrollHeight;
    };

    const upload = async (file) => {
        if (!file || uploading) return;
        if (file.size > 16 * 1024 * 1024) { window.alert('Attachments are capped at 16MB.'); return; }
        set_uploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${cs_base()}/api/v1/comms/channels/${channel_id}/media`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'X-Requested-With': 'cs-analysis-csp' },
                body: fd,
            });
            if (!res.ok) throw new Error('upload ' + res.status);
            // Keep a preview source: the server row alone cannot show the rep what they just recorded.
            set_pending_media(with_preview(await res.json(), file));
        } catch (e) { window.alert('Could not upload: ' + (e && e.message ? e.message : 'unknown error')); }
        set_uploading(false);
        if (file_ref.current) file_ref.current.value = '';
    };
    // ⏺ screen recording with voice (parity with the client panel, Nikhil
    // 2026-09-14). The clip goes through the same upload() as the 📎, so it is
    // the pending attachment and travels with the message like a voice note.
    const screen = use_screen_recorder(upload);

    // ── Annotate an image (client's or ours) and reply with the marked-up copy ──
    // The layer fetches its own blob (thumbnail / lightbox object URLs are
    // revoked when they unmount) and Done yields a PNG at source resolution.
    const open_annotator = async (a) => {
        if (!a || uploading) return;
        try {
            const url = URL.createObjectURL(await fetch_media_blob(a.message_id, a.idx));
            set_lightbox(null);
            set_annotating({ ...a, url });
        } catch (e) { window.alert('Could not load the image to annotate.'); }
    };
    const close_annotator = () => {
        set_annotating((cur) => { if (cur && cur.url) URL.revokeObjectURL(cur.url); return null; });
    };
    // Multipart upload with replyToId + meta {kind:'annotation', ofMessageId}
    // per the unified-chat contract — the backend posts it as a reply to the
    // original, so the thread shows before + after. A backend that only
    // stages the upload (returns {mediaId} without a seq) gets the message
    // completed through POST /messages carrying the same fields.
    const send_annotation = async (out, of) => {
        if (!out || !out.file || !of) return;
        close_annotator();
        set_uploading(true);
        try {
            const fields = annotation_upload_fields(of.message_id);
            const fd = new FormData();
            fd.append('file', out.file, out.filename);
            fd.append('replyToId', fields.replyToId);
            fd.append('meta', fields.meta);
            const res = await fetch(`${cs_base()}/api/v1/comms/channels/${channel_id}/media`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'X-Requested-With': 'cs-analysis-csp' },
                body: fd,
            });
            if (!res.ok) throw new Error('upload ' + res.status);
            const resp = await res.json();
            if (!upload_posted_message(resp)) {
                if (!resp || !resp.mediaId) throw new Error('upload returned neither a message nor a mediaId');
                await cs_analysis_fetch({
                    path: `/api/v1/comms/channels/${channel_id}/messages`,
                    method: 'POST',
                    body: { body: '', visibility: 'CLIENT_VISIBLE', replyToId: fields.replyToId, mediaIds: [resp.mediaId], meta: JSON.parse(fields.meta) },
                });
            }
            set_notice('✓ Annotated image sent as a reply to the original.');
            await load(last_seq);
            if (on_sent) on_sent();
        } catch (e) { window.alert('Could not send the annotation: ' + ((e && e.message) || 'unknown error')); }
        set_uploading(false);
    };

    const send = async () => {
        const body = draft.trim();
        if ((!body && !pending_media) || busy) return;
        set_busy(true);
        try {
            if (send_override) {
                await send_override(body);
            } else {
                await cs_analysis_fetch({
                    path: `/api/v1/comms/channels/${channel_id}/messages`,
                    method: 'POST',
                    body: {
                        body,
                        visibility: compose_visibility,
                        ...(reply_to ? { replyToId: reply_to.id } : {}),
                        ...(pending_media ? { mediaIds: [pending_media.mediaId] } : {}),
                    },
                });
            }
            if (accepted_ref.current) {
                sugg_feedback(
                    body === accepted_ref.current.text ? 'SENT_AS_IS' : 'EDITED_SENT',
                    accepted_ref.current,
                    { sentBody: body },
                );
                accepted_ref.current = null;
            } else if (suggestion && body) {
                // The ghost was on screen and the rep typed their own reply instead:
                // record what they sent (the suggester learns from the delta) and
                // clear the card — the inbound it answered is now answered.
                sugg_feedback('IGNORED_SENT_OWN', suggestion, { sentBody: body });
                set_suggestion(null);
            }
            set_draft('');
            set_recall_idx(NOT_RECALLING);
            set_grounded(null);
            set_reply_to(null);
            set_pending_media(null);
            await load(last_seq);
            if (on_sent) on_sent();
        } catch (e) { window.alert('Could not send: ' + (e && e.message ? e.message : 'unknown error')); }
        set_busy(false);
    };

    // Merge a tail snapshot without clobbering concurrent appends: fresh wins
    // per-id; anything newer than the snapshot's max seq is preserved.
    const merge_tail = (cur, fresh) => {
        if (!fresh.length) return cur;
        const max_fresh = fresh[fresh.length - 1].seq;
        const fresh_ids = new Set(fresh.map((m) => m.id));
        return [...fresh, ...cur.filter((m) => m.seq > max_fresh && !fresh_ids.has(m.id))];
    };

    // Reactions on ALREADY-loaded messages don't arrive via the seq cursor —
    // refresh the tail every 4th poll (60s) to pick up remote reactions/acks.
    const poll_n = useRef(0);
    useEffect(() => {
        if (!channel_id) return undefined;
        const t = setInterval(() => {
            poll_n.current += 1;
            if (poll_n.current % 4 === 0 && document.visibilityState === 'visible') {
                cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/messages`, query: { tail: 1, limit: 200 } })
                    .then((r) => { if (r && r.messages && r.messages.length) set_messages((cur) => merge_tail(cur, r.messages)); })
                    .catch(() => {});
            }
        }, 15000);
        return () => clearInterval(t);
    }, [channel_id]);

    const react = async (m, emoji) => {
        try {
            await cs_analysis_fetch({ path: `/api/v1/comms/messages/${m.id}/react`, method: 'POST', body: { emoji } });
            const r = await cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/messages`, query: { tail: 1, limit: 200 } });
            if (r && r.messages) set_messages((cur) => merge_tail(cur, r.messages));
        } catch (e) { /* best-effort */ }
    };

    const refresh_tail = async () => {
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/messages`, query: { tail: 1, limit: 200 } });
            if (r && r.messages) set_messages((cur) => merge_tail(cur, r.messages));
        } catch (e) { /* next poll */ }
    };

    const save_edit = async () => {
        try {
            await cs_analysis_fetch({ path: `/api/v1/comms/messages/${editing.id}/edit`, method: 'POST', body: { body: edit_draft.trim() } });
            set_messages((cur) => cur.map((x) => (x.id === editing.id ? { ...x, body: edit_draft.trim(), editedAt: new Date().toISOString() } : x)));
            set_editing(null);
        } catch (e) { window.alert((e && e.message) || 'edit failed'); }
    };

    const del_message = async (m) => {
        if (!window.confirm('Delete this message for everyone?')) return;
        try {
            await cs_analysis_fetch({ path: `/api/v1/comms/messages/${m.id}`, method: 'DELETE' });
            set_messages((cur) => cur.map((x) => (x.id === m.id ? { ...x, deletedAt: new Date().toISOString() } : x)));
        } catch (e) { window.alert((e && e.message) || 'delete failed'); }
    };

    // The alerter missed this inbound message: persist it as a REP_REPORTED
    // alert so the intent judge is tuned against it (cs-analysis
    // docs/design-escalation-alerts.md). Confirmed via the transient notice.
    const report_missed = async () => {
        if (!report_msg || report_busy) return;
        set_report_busy(true);
        try {
            await cs_analysis_fetch({
                path: '/api/v1/alerts/report-missed', method: 'POST',
                body: { channelId: channel_id, seq: report_msg.seq, ...(report_note.trim() ? { note: report_note.trim() } : {}) },
            });
            set_report_msg(null);
            set_report_note('');
            set_notice('✓ Reported as a missed escalation — it now trains the alert judge.');
        } catch (e) { window.alert('Could not report: ' + ((e && e.message) || 'unknown error')); }
        set_report_busy(false);
    };

    const start_forward = async (m) => {
        set_forward_msg(m);
        if (forward_targets == null && store_id) {
            try {
                const r = await cs_analysis_fetch({ path: `/api/v1/comms/clients/${store_id}/channels` });
                set_forward_targets(((r && r.channels) || []).filter((c) => c.kind === 'CLIENT_GROUP' && c.id !== channel_id));
            } catch (e) { set_forward_targets([]); }
        }
    };

    const do_forward = async (target_id) => {
        try {
            await cs_analysis_fetch({ path: `/api/v1/comms/messages/${forward_msg.id}/forward`, method: 'POST', body: { toChannelId: target_id } });
            set_forward_msg(null);
        } catch (e) { window.alert((e && e.message) || 'forward failed'); }
    };

    const toggle_record = async () => {
        if (recording) {
            recorder_ref.current && recorder_ref.current.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
            const chunks = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                set_recording(false);
                const blob = new Blob(chunks, { type: 'audio/webm' });
                if (blob.size < 800) return; // accidental tap
                await upload(new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' }));
            };
            recorder_ref.current = rec;
            rec.start();
            set_recording(true);
        } catch (e) { window.alert('Microphone unavailable: ' + ((e && e.message) || '')); }
    };

    const by_id = {};
    messages.forEach((m) => { by_id[m.id] = m; });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height }}>
            <div ref={scroll_ref} onScroll={on_scroll} style={{ flex: 1, overflowY: 'auto', padding: '4px 2px', background: '#fafbfc', borderRadius: 6, position: 'relative' }}>
                {loading_older ? <div style={{ textAlign: 'center', padding: 4 }}><Spinner size='sm' /></div> : null}
                {history_done && messages.length ? <div style={{ textAlign: 'center', fontSize: 10, color: '#b0b8bd', padding: 2 }}>· start of conversation ·</div> : null}
                {!loaded ? <div style={{ padding: 12 }}><Spinner size='sm' /> loading…</div> : null}
                {loaded && messages.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, height: '96%', color: '#a4b0bb' }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }}>💬</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#7f8c9d' }}>No messages yet</div>
                        <div style={{ fontSize: 12, marginTop: 2 }}>Start the conversation below — the client sees client-visible replies instantly.</div>
                    </div>
                ) : null}
                {messages.map((m) => {
                    const sys = m.authorType === 'SYSTEM';
                    const internal_msg = m.visibility === 'INTERNAL';
                    const quoted = m.replyToId ? by_id[m.replyToId] : null;
                    const media_refs = Array.isArray(m.mediaRefs) ? m.mediaRefs : [];
                    const is_mine = me_id && m.authorType === 'STAFF' && m.authorId === me_id;
                    const on_annotate = (a) => open_annotator(a);
                    // System lines (ticket milestones, "asked for a person") — centred, quiet.
                    if (sys) {
                        const s = system_meta(m);
                        return (
                            <div key={m.id} id={`comms-msg-${channel_id}-${m.seq}`} style={{ textAlign: 'center', margin: '8px 4px', fontSize: 11, color: '#7f8c8d' }}>
                                <span style={{ display: 'inline-block', background: s.kind === 'needs_human' ? '#fff8e6' : '#eef2f6', border: '1px solid ' + (s.kind === 'needs_human' ? '#f0d48a' : '#e3e8ec'), borderRadius: 10, padding: '2px 10px', maxWidth: '90%' }}>
                                    {s.kind === 'needs_human' ? '🙋 ' : s.kind === 'ticket_created' ? '🎫 ' : s.kind === 'whatsapp_linked' ? '🔗 ' : ''}
                                    {s.href ? <a href={s.href} target='_blank' rel='noreferrer noopener' title='Open the ticket' style={{ fontWeight: 600 }}>{s.text} ↗</a>
                                        : s.kind === 'whatsapp_linked' ? (
                                            // The user linked their own WhatsApp number (their chats now show the verified tick);
                                            // each group the number joined links to its conversation in the inbox.
                                            <>
                                                <span style={{ fontWeight: 600 }}>{s.lead}</span>
                                                {s.groups.length ? (
                                                    <>
                                                        {' · added to '}
                                                        {s.groups.map((g, i) => (
                                                            <React.Fragment key={`${g.channelId || g.title}:${i}`}>
                                                                {i ? ', ' : ''}
                                                                {g.href ? <a href={g.href} target='_blank' rel='noreferrer noopener' title='Open this group in the Chat inbox'>{g.title}</a> : g.title}
                                                            </React.Fragment>
                                                        ))}
                                                    </>
                                                ) : null}
                                            </>
                                        ) : s.text}
                                    <span style={{ marginLeft: 8, color: '#b0b8bd' }}>{fmt_ts(m.createdAt)}</span>
                                </span>
                            </div>
                        );
                    }
                    // The Infurnia Assistant's answer to the client (USER_CHAT): what
                    // the client was told, what it rested on, and how they rated it.
                    const ai = assistant_meta(m);
                    if (ai) {
                        return (
                            <div key={m.id} id={`comms-msg-${channel_id}-${m.seq}`} style={{ margin: '6px 4px', padding: '6px 10px', borderRadius: 8, fontSize: 13, background: '#f7f4ff', border: '1px solid #ddd2f6' }}>
                                <div style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 2 }}>
                                    <b style={{ color: '#5b3fa0' }}>{m.authorName || 'Infurnia Assistant'}</b>
                                    <span style={{ marginLeft: 6, fontSize: 9.5, color: '#6c4bb5', background: '#ece4ff', border: '1px solid #d6c8f5', borderRadius: 8, padding: '0 6px', fontWeight: 700, letterSpacing: 0.4 }}>Assistant</span>
                                    {ai.confidence ? (
                                        <span title="The assistant's own confidence in this answer" style={{ marginLeft: 6, color: ai.confidence === 'high' ? '#2e7d4f' : ai.confidence === 'medium' ? '#8fa3c0' : '#b9770e' }}>
                                            · {ai.confidence} confidence
                                        </span>
                                    ) : null}
                                    {ai.fromMedia ? (() => {
                                        const q = ai.questionMessageId ? messages.find((x) => x.id === ai.questionMessageId) : null;
                                        const mt = q && Array.isArray(q.mediaRefs) && q.mediaRefs[0] ? q.mediaRefs[0].mimetype : null;
                                        return <span title='The client sent a voice note / recording / screenshot; the assistant answered what it heard or saw' style={{ marginLeft: 6, color: '#8fa3c0' }}>· {from_media_note(ai.fromMedia, mt)}</span>;
                                    })() : null}
                                    {ai.conversational ? (
                                        <span title='The assistant handled this turn itself — no knowledge fact was needed' style={{ marginLeft: 6, color: '#8fa3c0' }}>· 💬 {triage_label(ai.triage)}</span>
                                    ) : !ai.grounded ? (
                                        <span title='Not grounded on a knowledge fact — check it before relying on it' style={{ marginLeft: 6, color: '#b9770e' }}>· ⚠ ungrounded</span>
                                    ) : null}
                                    {ai.via === 'backstop' ? <span title='No person replied inside the backstop window, so the assistant answered' style={{ marginLeft: 6, color: '#8fa3c0' }}>· ⏱ {BACKSTOP_NOTE}</span> : null}
                                    <span style={{ float: 'right' }}>
                                        {fmt_ts(m.createdAt)}
                                        {!m.deletedAt ? (
                                            <>
                                                <MsgAction onClick={() => set_reply_to(m)} title='Reply'>↩</MsgAction>
                                                {on_to_ticket ? <MsgAction onClick={() => on_to_ticket(m)} title='Raise a ticket from this message'>⛏</MsgAction> : null}
                                            </>
                                        ) : null}
                                    </span>
                                </div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{m.deletedAt ? <i>(deleted)</i> : m.body}</div>
                                {ai.citations.length ? (
                                    // The rep's job here is to judge what the client was told, so the
                                    // sources carry the same affordances as a draft suggestion's:
                                    // open the full fact, ✓ confirm it, ✗ flag it wrong or outdated —
                                    // instead of a link that leaves the thread (Nikhil, 2026-09-14).
                                    <div title='The product facts this answer was grounded on — confirm or flag each one'
                                        style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
                                        <span style={{ fontSize: 11, color: '#7f8c8d', whiteSpace: 'nowrap' }}>grounded on:</span>
                                        {ai.citations.map((c) => (
                                            <KnowledgeChip key={c.id} unit={{ id: c.id, title: c.title }} channel_id={channel_id} for_seq={m.seq} />
                                        ))}
                                    </div>
                                ) : null}
                                {ai.feedback ? (
                                    <div title='The client rated this answer' style={{ fontSize: 11, marginTop: 4, color: ai.feedback.helpful ? '#2e7d4f' : '#c0392b' }}>
                                        {ai.feedback.helpful ? '👍 client found this helpful' : '👎 client found this unhelpful'}{ai.feedback.at ? ` · ${fmt_ts(ai.feedback.at)}` : ''}
                                    </div>
                                ) : null}
                                <Reactions message={m} on_react={react} />
                            </div>
                        );
                    }
                    const annot = annotation_of(m);
                    const revised = revision_of(m, by_id);
                    const quoted_img = quoted && !quoted.deletedAt ? first_image_ref(quoted) : null;
                    return (
                        <div key={m.id} id={`comms-msg-${channel_id}-${m.seq}`} style={{ margin: '6px 4px', padding: '6px 10px', borderRadius: 8, fontSize: 13,
                            background: internal_msg ? '#fff8e6' : '#fff',
                            border: '1px solid ' + (internal_msg ? '#f0d48a' : '#e8ecef') }}>
                            <div style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 2 }}>
                                <b style={{ color: '#34495e' }}>
                                    {m.authorName || m.authorType}
                                </b>
                                {m.authorType === 'STAFF' ? ' · Infurnia' : ''}
                                {m.authorType === 'CLIENT_USER' ? ' · client' : ''}
                                {m.authorType === 'GUEST' ? ' · guest (WhatsApp)' : ''}
                                {internal_msg ? <Badge color='warning' style={{ marginLeft: 6 }}>internal</Badge> : null}
                                {annot ? <span title='A marked-up copy of the quoted image' style={{ marginLeft: 6, fontSize: 9.5, color: '#2f6fed', background: '#eef2fb', border: '1px solid #c9d6f2', borderRadius: 8, padding: '0 6px', fontWeight: 600 }}>✎ annotated</span> : null}
                                <span style={{ float: 'right' }}>
                                    {m.editedAt ? <i style={{ marginRight: 4 }}>edited</i> : null}
                                    {fmt_ts(m.createdAt)}
                                    <WaTicks m={m} />
                                    {!m.deletedAt ? (
                                        <>
                                            <MsgAction onClick={() => set_reply_to(m)} title='Reply'>↩</MsgAction>
                                            {store_id ? <MsgAction onClick={() => start_forward(m)} title='Forward to another group of this org'>↪</MsgAction> : null}
                                            {on_to_ticket ? <MsgAction onClick={() => on_to_ticket(m)} title='Raise a ticket from this message'>⛏</MsgAction> : null}
                                            {m.body ? <MsgAction onClick={() => set_promote_msg(m)} title='Save as product / process knowledge'>💡</MsgAction> : null}
                                            {is_client_inbound(m) ? <MsgAction onClick={() => { set_report_msg(m); set_report_note(''); }} title='Missed escalation — report it so the alert judge learns' danger>⚠</MsgAction> : null}
                                            {is_mine ? (
                                                <>
                                                    <MsgAction onClick={() => { set_editing(m); set_edit_draft(m.body); }} title='Edit (WhatsApp allows ~15 min)'>✎</MsgAction>
                                                    <MsgAction onClick={() => del_message(m)} title='Delete for everyone' danger>🗑</MsgAction>
                                                </>
                                            ) : null}
                                        </>
                                    ) : null}
                                </span>
                            </div>
                            {m.replyToId ? (
                                <div style={{ fontSize: 11, color: '#7f8c8d', borderLeft: '3px solid #d6dbdf', padding: '2px 6px', margin: '2px 0 4px', background: '#f6f8f9', borderRadius: 4 }}>
                                    ↩ {annot ? 'annotated ' : ''}<b>{quoted ? (quoted.authorName || quoted.authorType) : 'earlier message'}</b>
                                    {quoted ? `: ${(quoted.deletedAt ? '(deleted)' : quoted.body || (quoted_img ? '🖼 image' : '(attachment)')).slice(0, 90)}` : ''}
                                    {/* Annotated reply: the original ("before") sits in the quote, the marked-up copy ("after") below. */}
                                    {annot && quoted_img ? (
                                        <div style={{ marginTop: 3 }}>
                                            <MediaImage message_id={quoted.id} idx={quoted_img.idx} filename={quoted_img.ref.filename}
                                                on_media_load={on_media_load} on_zoom={set_lightbox} size={{ w: 120, h: 90 }} />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {revised ? (
                                // Client "revise" (client panel, 2026-09-15): the client sent a NEW
                                // message instead of editing, so history stays and this row says which
                                // earlier one it supersedes. Rendered for any channel kind on meta alone
                                // — only client rows carry it. Click jumps to the original when loaded.
                                <div title={revised.original && revised.original.body ? revised.original.body : 'The client sent this as a revised version of an earlier message'}
                                    onClick={revised.original && revised.original.seq != null ? () => reveal_message(channel_id, revised.original.seq) : undefined}
                                    style={{ fontSize: 11, color: '#7f8c8d', margin: '0 0 3px', cursor: revised.original && revised.original.seq != null ? 'pointer' : 'default' }}>
                                    {revised.text}
                                </div>
                            ) : null}
                            {editing && editing.id === m.id ? (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Input type='textarea' rows={2} value={edit_draft} onChange={(e) => set_edit_draft(e.target.value)} style={{ fontSize: 13 }} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <Button size='sm' color='primary' onClick={save_edit} disabled={!edit_draft.trim()}>Save</Button>
                                        <Button size='sm' color='secondary' outline onClick={() => set_editing(null)}>✕</Button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ whiteSpace: 'pre-wrap' }}>{m.deletedAt ? <i>(deleted)</i> : render_body_with_mentions(m.body, m.waMentions)}</div>
                            )}
                            {!m.deletedAt && media_refs.map((ref, idx) => (
                                <Attachment key={idx} message_id={m.id} idx={idx} media_ref={ref} insight={insight_for(m, idx)}
                                    on_media_load={on_media_load} on_zoom={set_lightbox} on_annotate={on_annotate} />
                            ))}
                            <Reactions message={m} on_react={react} />
                        </div>
                    );
                })}
                <div ref={bottom_ref} />
            </div>
            {lightbox ? <Lightbox items={gallery} start={lightbox} on_close={() => set_lightbox(null)} on_annotate={open_annotator} /> : null}
            {annotating ? (
                <AnnotateImage src={annotating.url} filename={annotating.filename}
                    on_cancel={close_annotator}
                    on_done={(out) => send_annotation(out, annotating)} />
            ) : null}
            {!at_bottom ? (
                <div onClick={jump_to_bottom} title='Jump to latest'
                    style={{ alignSelf: 'flex-end', marginTop: -34, marginRight: 14, zIndex: 5, width: 28, height: 28,
                        borderRadius: 14, background: '#fff', border: '1px solid #d5dbe1', boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#5f6b7a', fontSize: 14 }}>
                    ↓
                </div>
            ) : null}
            {forward_msg ? (
                <div style={{ fontSize: 11, color: '#7f8c8d', background: '#f6f8f9', borderRadius: 4, padding: '3px 8px', marginTop: 6 }}>
                    ↪ forward to:{' '}
                    {forward_targets == null ? 'loading…' : forward_targets.length === 0 ? 'no other groups' :
                        forward_targets.map((t) => (
                            <span key={t.id} onClick={() => do_forward(t.id)}
                                style={{ cursor: 'pointer', color: '#2f6fed', marginRight: 8, textDecoration: 'underline' }}>
                                {t.groupKey || t.title}
                            </span>
                        ))}
                    <span onClick={() => set_forward_msg(null)} style={{ cursor: 'pointer', float: 'right', color: '#e74c3c' }}>✕</span>
                </div>
            ) : null}
            {reply_to ? (
                <div style={{ fontSize: 11, color: '#7f8c8d', background: '#f6f8f9', borderRadius: 4, padding: '3px 8px', marginTop: 6 }}>
                    ↩ replying to <b>{reply_to.authorName || reply_to.authorType}</b>: {(reply_to.body || '(attachment)').slice(0, 60)}
                    <span onClick={() => set_reply_to(null)} style={{ cursor: 'pointer', float: 'right', color: '#e74c3c' }}>✕</span>
                </div>
            ) : null}
            {pending_media ? <PendingAttachment media={pending_media} on_clear={() => set_pending_media(null)} on_zoom={set_lightbox} /> : null}
            {report_msg ? (
                <div style={{ fontSize: 11, color: '#7f8c8d', background: '#fff4f2', border: '1px solid #f3d3cd', borderRadius: 4, padding: '3px 8px', marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                        ⚠ missed escalation — <b>{report_msg.authorName || report_msg.authorType}</b>: {cut_text(report_msg.body || '(attachment)', 60)}
                    </span>
                    <Input bsSize='sm' value={report_note} placeholder='Why is this an escalation? (optional)' autoFocus
                        onChange={(e) => set_report_note(e.target.value)}
                        onKeyDown={(e) => {
                            // The composer's window listener treats Tab / Esc as accept / dismiss.
                            e.stopPropagation();
                            if (e.key === 'Enter') { e.preventDefault(); report_missed(); }
                            else if (e.key === 'Escape') { e.preventDefault(); set_report_msg(null); }
                        }}
                        style={{ fontSize: 11, padding: '2px 6px', height: 24, flex: 1, minWidth: 160 }} />
                    <Button size='sm' color='danger' outline style={{ fontSize: 11, padding: '0 8px' }} disabled={report_busy} onClick={report_missed}>
                        {report_busy ? <Spinner size='sm' style={{ width: 10, height: 10 }} /> : 'Report'}
                    </Button>
                    <span onClick={() => set_report_msg(null)} style={{ cursor: 'pointer', color: '#e74c3c' }}>✕</span>
                </div>
            ) : null}
            {mention_q != null && mention_groups.length ? (
                <div style={{ background: '#fff', border: '1px solid #d5dbe1', borderRadius: 6, marginTop: 6, maxHeight: 180, overflowY: 'auto', boxShadow: '0 -2px 8px rgba(0,0,0,0.08)' }}>
                    {mention_groups.map((g) => (
                        <React.Fragment key={g.label}>
                            <div style={{ padding: '5px 10px 1px', fontSize: 9.5, letterSpacing: 0.7, textTransform: 'uppercase', color: '#8a97a5', fontWeight: 700 }}>{g.label}</div>
                            {g.members.map((m) => (
                                <div key={g.label + ':' + m.name}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        set_draft((d) => d.replace(/@([^@\s]*)$/, '@' + m.name + ' '));
                                        set_mention_q(null);
                                        if (input_ref.current) input_ref.current.focus();
                                    }}
                                    style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eef4ff'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                                    @{m.name} <span style={{ color: '#95a5a6', fontSize: 10 }}>· {mention_kind_label(m.kind)}</span>
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            ) : null}
            {notice ? (
                <div style={{ marginTop: 8, fontSize: 11.5, color: '#2e7d4f', background: '#eefaf2', border: '1px solid #cfeedd', borderRadius: 8, padding: '5px 10px' }}>{notice}</div>
            ) : null}
            {screen.note ? (
                <div style={{ marginTop: 8, fontSize: 11.5, color: '#8a6d1f', background: '#fff8e6', border: '1px solid #f1e2b3', borderRadius: 8, padding: '5px 10px' }}>
                    ⏺ {screen.note}
                    <span onClick={screen.clear_note} style={{ cursor: 'pointer', float: 'right', color: '#e74c3c' }}>✕</span>
                </div>
            ) : null}
            {promote_msg ? (
                <PromoteKnowledgeModal channel_id={channel_id} message={promote_msg}
                    on_close={() => set_promote_msg(null)}
                    on_saved={(r) => {
                        set_promote_msg(null);
                        set_notice(r && r.existing
                            ? '✓ Already in the knowledge base — your confirmation was recorded on the existing fact.'
                            : '✓ Saved as knowledge (draft). It can now ground reply suggestions.');
                    }} />
            ) : null}
            {suggestion && !draft.trim() ? (
                <div onClick={accept_suggestion} title='Tab (or click) to use · Esc to dismiss'
                    style={{ marginTop: 8, padding: '8px 12px', background: '#f4f8ff', border: '1px solid #d9e6ff', borderRadius: 10, cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 14 }}>✨</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: '#33414e', whiteSpace: 'pre-wrap' }}>{suggestion.text}</div>
                        <div style={{ fontSize: 10.5, color: suggestion.confidence === 'low' ? '#b9770e' : '#8fa3c0', marginTop: 3 }}>
                            {suggestion.confidence === 'low' ? '⚠ low confidence — review carefully · ' : ''}
                            AI suggestion{suggestion.knowledgeCount ? ` · ${suggestion.knowledgeCount} product fact${suggestion.knowledgeCount > 1 ? 's' : ''}` : ''}{suggestion.evidenceCount ? ` · ${suggestion.evidenceCount} from this org` : ''}{!suggestion.knowledgeCount && !suggestion.evidenceCount ? ' · from this conversation' : ''} — <b>Tab</b> to use, <b>Enter</b> to send
                        </div>
                        {suggestion.knowledge && suggestion.knowledge.length ? (
                            <div onClick={(e) => e.stopPropagation()} title='The product facts this draft is grounded on — confirm or flag each one'
                                style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5, cursor: 'default' }}>
                                {suggestion.knowledge.map((k) => <KnowledgeChip key={k.id} unit={k} channel_id={channel_id} for_seq={suggestion.forSeq} />)}
                            </div>
                        ) : null}
                    </div>
                    <span onClick={(e) => { e.stopPropagation(); dismiss_suggestion(); }} title='Dismiss'
                        style={{ color: '#a9bad2', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>✕</span>
                </div>
            ) : sugg_loading && !draft.trim() ? (
                <div style={{ marginTop: 8, fontSize: 11, color: '#a9bad2' }}>✨ drafting a suggestion…</div>
            ) : null}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'flex-end' }}>
                <Input
                    type='textarea' rows={2} value={draft} placeholder={placeholder || 'Message…'}
                    innerRef={input_ref}
                    onChange={(e) => {
                        const v = e.target.value;
                        set_draft(v);
                        set_recall_idx(NOT_RECALLING); // "any typing exits recall mode"
                        // Accepted draft cleared → its citation chips go too.
                        if (grounded && !v.trim()) set_grounded(null);
                        // Trailing @word (no space yet) → autocomplete mode.
                        const m = v.match(/@([^@\s]*)$/);
                        set_mention_q(m ? m[1] : null);
                    }}
                    onKeyDown={(e) => {
                        // WhatsApp semantics: Enter sends, Shift+Enter = newline
                        // (Ctrl/Cmd+Enter still works). Mention picker eats Enter.
                        if (e.key === 'Enter' && !e.shiftKey && mention_q === null) { e.preventDefault(); send(); }
                        else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
                        // Escape closes the picker and exits recall mode (the recalled
                        // text stays as an ordinary draft).
                        if (e.key === 'Escape') { set_mention_q(null); set_recall_idx(NOT_RECALLING); }
                        // ↑ recalls the rep's last message, ↑↑ the one before, ↓ walks
                        // back to the empty box. Only when the box is empty or the caret
                        // is on the first/last line of the recalled entry — otherwise
                        // (or with the picker open) the arrows move the caret as usual.
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            const el = e.target;
                            const step = recall_move(e.key, {
                                idx: recall_idx, history: recall_list, draft, mention_open: mention_q !== null,
                                caret: { first_line: caret_on_first_line(el.value, el.selectionStart), last_line: caret_on_last_line(el.value, el.selectionEnd) },
                            });
                            if (step) { e.preventDefault(); set_recall_idx(step.idx); set_draft(step.draft); }
                        }
                    }}
                    style={{ fontSize: 13 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {allow_internal ? (
                        <label style={{ fontSize: 11, color: internal ? '#b8860b' : '#7f8c8d', whiteSpace: 'nowrap', margin: 0 }}>
                            <input type='checkbox' checked={internal} onChange={(e) => set_internal(e.target.checked)} style={{ marginRight: 4 }} />
                            internal note
                        </label>
                    ) : (
                        <span style={{ fontSize: 10, color: '#95a5a6', whiteSpace: 'nowrap' }}>visible to client</span>
                    )}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                        {!send_override ? (
                            <>
                                <Button color='light' title='Suggest a reply with AI' disabled={sugg_loading}
                                    style={{ fontSize: 15, padding: '4px 10px', border: '1px solid #dde3e8', borderRadius: 8, background: '#fff' }}
                                    onClick={request_suggestion}>
                                    {sugg_loading ? <Spinner size='sm' /> : '✨'}
                                </Button>
                                <input ref={file_ref} type='file' style={{ display: 'none' }}
                                    onChange={(e) => upload(e.target.files && e.target.files[0])} />
                                <Button color='light' title='Attach a file' disabled={uploading || !!pending_media}
                                    style={{ fontSize: 15, padding: '4px 10px', border: '1px solid #dde3e8', borderRadius: 8, background: '#fff' }}
                                    onClick={() => file_ref.current && file_ref.current.click()}>
                                    {uploading ? <Spinner size='sm' /> : '📎'}
                                </Button>
                                {/* 🎙 and ⏺ are mutually exclusive (one MediaRecorder at a time), and
                                    both render only where the browser can actually do it — on a page
                                    Chrome does not treat as a secure context `mediaDevices` is undefined
                                    and the button could only throw. The live one stays enabled so it
                                    can be stopped. */}
                                {voice_note_supported() ? (
                                    <Button color={recording ? 'danger' : 'light'}
                                        title={recording ? 'Stop recording' : 'Record a voice note'}
                                        style={{ fontSize: 15, padding: '4px 10px', border: recording ? undefined : '1px solid #dde3e8', borderRadius: 8, background: recording ? undefined : '#fff' }}
                                        disabled={!recording && (uploading || !!pending_media || screen.recording)} onClick={toggle_record}>
                                        {recording ? '■' : '🎙'}
                                    </Button>
                                ) : null}
                                {screen.supported ? (
                                    <Button color={screen.recording ? 'danger' : 'light'}
                                        title={screen.recording ? STOP_SCREEN_TITLE : RECORD_SCREEN_TITLE}
                                        style={{ fontSize: 15, padding: '4px 10px', border: screen.recording ? undefined : '1px solid #dde3e8', borderRadius: 8, background: screen.recording ? undefined : '#fff', whiteSpace: 'nowrap' }}
                                        disabled={!screen.recording && (uploading || !!pending_media || recording)} onClick={screen.toggle}>
                                        {screen.recording ? `■ ${fmt_elapsed(screen.elapsed_ms)}` : '⏺'}
                                    </Button>
                                ) : null}
                            </>
                        ) : null}
                        <Button color='primary' disabled={busy || (!draft.trim() && !pending_media)} onClick={send}
                            style={{ fontSize: 13.5, fontWeight: 600, padding: '4px 18px', borderRadius: 8 }}>
                            Send ➤
                        </Button>
                    </div>
                </div>
            </div>
            {grounded && draft.trim() && grounded.knowledge && grounded.knowledge.length ? (
                <div title='The product facts the accepted draft is grounded on — confirm or flag each one'
                    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 5, fontSize: 10.5, color: '#8fa3c0' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>✨ grounded on:</span>
                    {grounded.knowledge.map((k) => <KnowledgeChip key={k.id} unit={k} channel_id={channel_id} for_seq={grounded.forSeq} />)}
                </div>
            ) : null}
        </div>
    );
}

// ── Per-conversation media gallery ──────────────────────────────────────────

function GalleryTile({ item, on_open }) {
    const [url, set_url] = useState(null);
    const [failed, set_failed] = useState(false);
    useEffect(() => {
        if (item.kind !== 'image') return undefined;
        let revoke = null; let alive = true;
        fetch_media_blob(item.message_id, item.idx)
            .then((b) => { if (!alive) return; revoke = URL.createObjectURL(b); set_url(revoke); })
            .catch(() => { if (alive) set_failed(true); });
        return () => { alive = false; if (revoke) URL.revokeObjectURL(revoke); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.key]);
    return (
        <div onClick={on_open}
            title={`${item.filename || item.kind} · ${fmt_ts(item.createdAt)}${item.authorName ? ' · ' + item.authorName : ''}`}
            style={{ height: 104, borderRadius: 6, overflow: 'hidden', background: item.kind === 'video' ? '#0b0e11' : '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            {item.kind === 'video' ? <span style={{ color: '#fff', fontSize: 24 }}>▶</span>
                : failed ? <span style={{ fontSize: 10, color: '#95a5a6' }}>n/a</span>
                    : !url ? <Spinner size='sm' />
                        : <img src={url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
    );
}

function GalleryFileRow({ item }) {
    const [busy, set_busy] = useState(false);
    const download = async () => {
        if (busy) return;
        set_busy(true);
        try {
            const blob = await fetch_media_blob(item.message_id, item.idx);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = item.filename || 'attachment';
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 30000);
        } catch (e) { window.alert('Could not download'); }
        set_busy(false);
    };
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #f4f6f8', fontSize: 12 }}>
            <span>{item.kind === 'audio' ? '🎤' : '📎'}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.filename || '(file)'}</span>
            <span style={{ color: '#95a5a6', whiteSpace: 'nowrap' }}>{fmt_ts(item.createdAt)}</span>
            <Button size='sm' color='link' style={{ padding: 0, fontSize: 12 }} disabled={busy} onClick={download}>{busy ? '…' : '↓'}</Button>
        </div>
    );
}

/** WhatsApp-style "Media" section for one conversation: every media-bearing
 *  message (GET /channels/:id/media), images/videos as tiles opening the
 *  navigable Lightbox, other files as download rows. `on_jump` (optional)
 *  jumps the thread to the media's message. */
export function MediaGallery({ channel_id, open, on_close, on_jump }) {
    const [rows, set_rows] = useState(null);
    const [start, set_start] = useState(null);
    useEffect(() => {
        if (!open) return;
        set_rows(null); set_start(null);
        cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel_id}/media`, query: { limit: 200 } })
            .then((r) => set_rows((r && r.messages) || []))
            .catch(() => set_rows([]));
    }, [channel_id, open]);
    if (!open) return null;
    const visual = [];
    const files = [];
    for (const m of rows || []) {
        (Array.isArray(m.mediaRefs) ? m.mediaRefs : []).forEach((ref, i) => {
            const mm = ((ref && ref.mimetype) || '').toLowerCase();
            const item = {
                key: `${m.id}:${i}`, message_id: m.id, idx: i, filename: ref.filename,
                size: ref.size, seq: m.seq, createdAt: m.createdAt, authorName: m.authorName,
            };
            if (mm.indexOf('image/') === 0) visual.push({ ...item, kind: 'image' });
            else if (mm.indexOf('video/') === 0) visual.push({ ...item, kind: 'video' });
            else if (mm.indexOf('audio/') === 0) files.push({ ...item, kind: 'audio' });
            else files.push({ ...item, kind: 'file' });
        });
    }
    return (
        <div onClick={on_close} style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.5)', zIndex: 1500, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()}
                style={{ width: 'min(520px, 92vw)', height: '100%', background: '#fff', boxShadow: '-4px 0 18px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #eef1f4' }}>
                    <b style={{ fontSize: 14 }}>Media{rows ? ` · ${visual.length + files.length}` : ''}</b>
                    <span onClick={on_close} title='Close' style={{ marginLeft: 'auto', cursor: 'pointer', color: '#95a5a6', fontSize: 16, lineHeight: 1 }}>✕</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                    {rows === null ? <Spinner size='sm' /> : (
                        <>
                            {visual.length === 0 && files.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#95a5a6' }}>No media in this conversation yet.</div>
                            ) : null}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 6 }}>
                                {visual.map((it) => <GalleryTile key={it.key} item={it} on_open={() => set_start(it)} />)}
                            </div>
                            {files.length ? (
                                <div style={{ marginTop: 14 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Files & voice notes</div>
                                    {files.map((it) => <GalleryFileRow key={it.key} item={it} />)}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
            {start ? (
                <Lightbox items={visual} start={start} on_close={() => set_start(null)}
                    on_jump={on_jump ? (seq) => { set_start(null); on_close(); on_jump(seq); } : null} />
            ) : null}
        </div>
    );
}
